import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialog,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { DocumentService } from '@services/api/document/document.service';
import { RevisionDataService } from '@services/data/revision-data-service';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { DropzoneComponent } from '@shared/dropzone/dropzone.component';
import { CheckboxComponent } from '@shared/checkbox/checkbox.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import { DuplicateDocumentsDialogComponent } from '@main/dialogs/duplicate-documents-dialog/duplicate-documents-dialog.component';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { convertToFileList } from '@helpers/file';
import { DiscrepancyIdNo } from '@models/discrepancy/item';
import { FileType } from '@constants/file';
import { ImageConversionService } from '@services/image-conversion.service';
import { extractImageMetadata, ImageMetadata } from '@helpers/image-metadata';
import DATA_QA from '@automation/data-qa.json';

@Component({
    templateUrl: 'upload-correction-dialog.component.html',
    imports: [
        CommonModule,
        MatIconModule,
        FlatButtonComponent,
        DropzoneComponent,
        CheckboxComponent,
        LoaderComponent,
    ],
})
export class UploadCorrectionDialogComponent implements OnInit, OnDestroy {
    @ViewChild('dropzone') dropzone!: DropzoneComponent;

    acceptedFiles: FileType[] = [FileType.PDF, FileType.JPEG, FileType.PNG];
    files: FileList | null = null;
    isConverting = false;
    selectedDiscrepancies: number[] = [];
    progress: number | null = null;
    error: boolean = false;
    loading: boolean = false;
    fileSizeError = false;
    revisionList: string[] = [];
    uploadedFileExt: string = '';
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    readonly dataQa = DATA_QA;
    readonly maxFileSizePdfMB = 105;
    readonly maxFileSizeImageMB = 100;

    readonly data: UploadCorrectionDialogData = inject(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(
        MatDialogRef<UploadCorrectionDialogComponent>,
    );
    private readonly dialog = inject(MatDialog);
    private readonly documentService = inject(DocumentService);
    private readonly revisionDataService = inject(RevisionDataService);
    private readonly imageConversionService = inject(ImageConversionService);
    private imageMetadataForUpload: ImageMetadata | null = null;

    ngOnInit(): void {
        if (this.data.file) {
            this.files = convertToFileList([this.data.file]);
        }

        this.setCorrectionNameList();
    }

    ngOnDestroy() {
        this._subscriptions.unsubscribeAllSafe();
    }

    onFileReady(files: FileList) {
        this.error = false;
        this.fileSizeError = false;

        if (!files) {
            this.cancelUpload();
            return;
        }

        if (this.getIsError()) {
            this.files = null;
            return;
        }

        this.files = files;
    }

    async onUploadClick() {
        if (!this.files?.[0]) return;

        let file = this.files[0];

        if (!this.validateFileSize(file)) {
            this.fileSizeError = true;
            return;
        }

        const meta = await this.convertIfNeeded(file);
        file = this.files[0];

        if (meta && file.size > this.maxFileSizePdfMB * 1024 * 1024) {
            this.fileSizeError = true;
            return;
        }

        this.uploadedFileExt = file.name.split('.').pop() ?? '';
        if (this.checkDuplicateFile(file)) {
            if (meta) this.imageMetadataForUpload = meta;
            this.askForNewFileName();
            return;
        }

        if (meta) this.imageMetadataForUpload = meta;
        this.upload();
    }

    private validateFileSize(file: File): boolean {
        const isImage = this.imageConversionService.needsConversion(file);
        const maxSizeBytes = isImage
            ? this.maxFileSizeImageMB * 1024 * 1024
            : this.maxFileSizePdfMB * 1024 * 1024;
        return file.size <= maxSizeBytes;
    }

    private async convertIfNeeded(file: File): Promise<ImageMetadata | null> {
        if (!this.imageConversionService.needsConversion(file)) {
            return null;
        }
        try {
            this.isConverting = true;
            const imageMetadata = await extractImageMetadata(file);
            const pdfFile =
                await this.imageConversionService.convertToPdf(file);
            this.files = convertToFileList([pdfFile]);
            return imageMetadata;
        } catch (error) {
            console.error('Image conversion failed:', error);
            this.error = true;
            return null;
        } finally {
            this.isConverting = false;
        }
    }

    onCancelClick() {
        this.cancelUpload();
        this.dialogRef.close({ success: false });
    }

    onDiscrepancySelected(checked: boolean, discrepancyId: number) {
        this.selectedDiscrepancies = checked
            ? [...this.selectedDiscrepancies, discrepancyId]
            : [
                  ...this.selectedDiscrepancies.filter(
                      (disc) => disc !== discrepancyId,
                  ),
              ];
    }

    private setCorrectionNameList() {
        this.revisionDataService.documentRevisions$.subscribe({
            next: (revisions) =>
                (this.revisionList = revisions!.corrections.map(
                    (item) => item.revisionName,
                )),
        });
    }

    private askForNewFileName() {
        const file = this.files![0];
        const fileName = file.name;
        const duplicateDocDialogRef = this.dialog.open(
            DuplicateDocumentsDialogComponent,
            {
                data: {
                    documentName: fileName,
                    revisionList: this.revisionList,
                },
                maxWidth: '450px',
                minWidth: '200px',
                width: '450px',
                disableClose: true,
            },
        );
        duplicateDocDialogRef.afterClosed().subscribe({
            next: (response) => {
                if (response.success) {
                    const { updatedDocumentName } = response;
                    const newFile = this.createFileWithName(
                        file,
                        updatedDocumentName,
                    );
                    const fileArray = [newFile];
                    this.files = convertToFileList(fileArray);
                    if (this.imageMetadataForUpload) {
                        this.imageMetadataForUpload.original.name =
                            newFile.name;
                    }
                    this.upload();
                }
            },
        });
    }

    private cancelUpload() {
        if (this.progress) {
            this.progress = null;
            this.loading = false;
            this._subscriptions.unsubscribeAllSafe();
        }
    }

    private upload() {
        if (this.files?.[0]) {
            this.error = false;
            this.loading = true;

            this._subscriptions['upload-document'] = this.documentService
                .uploadCorrection(
                    this.data.id,
                    this.prepareFormdata(this.files[0]),
                )
                .subscribe({
                    next: (response) => {
                        this.progress = response.progress;
                        if (response.status === 'success') {
                            this.dialogRef.close({
                                success: true,
                                fileName: this.files![0].name,
                            });
                        }
                    },
                    error: (err) => this.handleError(err),
                    complete: () => (this.loading = false),
                });
        }
    }

    private readonly prepareFormdata = (file: File) => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        this.selectedDiscrepancies.forEach((item) =>
            formData.append('discrepancy', item.toString()),
        );

        if (this.imageMetadataForUpload) {
            const payload = [this.imageMetadataForUpload];
            formData.append('imageMetadata', JSON.stringify(payload));
        }

        return formData;
    };

    private checkDuplicateFile(file: File): boolean {
        const fileName = file.name;
        const fileExist = this.revisionList.includes(fileName);

        return fileExist;
    }

    private createFileWithName(file: File, newName: string): File {
        const newFile = new File([file], newName, {
            type: file.type,
            lastModified: file.lastModified,
        });

        return newFile;
    }

    private handleError(error: HttpErrorResponse) {
        this.error = true;
        this.loading = false;

        if (error.status === HttpStatusCode.Conflict) {
            this.askForNewFileName();
        }
    }

    getIsError() {
        return !!this.dropzone?.errors?.length;
    }
}

interface UploadCorrectionDialogData {
    id: number;
    discrepancies: DiscrepancyIdNo[];
    file: File | null;
}
